import React, { SetStateAction, useEffect, useState } from 'react';

import {
    Contract,
    GetContractTermsInput,
    GetContractsByIdInput,
    GetDefaultContentAcquisitionEntityInput,
    GetDiscountRatesInput,
    GetPaperPlanMetadataInput,
    GetRatchetModelsByIdInput,
    GetRatchetModelsByIdOutput,
    GetRegionsByIdInput,
    GetRoyaltyModelTypesInput,
    GetServiceLaunchDateInput,
    GetTerritoryTermsInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { isEqual } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useHistory, useLocation } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { BreadcrumbWrapper } from 'src/components/Breadcrumb';
import { CoreRates } from 'src/components/RateCardDetail/CoreRates';
import { RateCardSaveProps, fullStepList, handlers } from 'src/components/RateCardDetail/Handlers/Handlers';
import { getFullTerritoryTerms } from 'src/components/RateCardDetail/utils/territoryTermsPaginator';
import { StepTracker } from 'src/components/StepTracker';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { ContractBreadcrumbTitle } from 'src/constants/enums';
import { MULTIPLIER_NAME_MAP, defaultIsRateCardInputValid, defaultRateCardValues } from 'src/constants/rateCards';
import { EDITOR_ROLE_SET } from 'src/constants/role';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { useRateCardDetail } from 'src/hooks';
import { useTitle } from 'src/hooks/useTitle';
import { IMetadata } from 'src/types/IMetadata';
import {
    IFirstPartyCorePlan,
    IMultiplierType,
    IPlanNameToMetadataObj,
    IRatchetModelIdtoRatchetsObj,
    IRateCardValues
} from 'src/types/IRateCard';
import { IStepTrackerStep } from 'src/types/IStepTrackerStep';
import { IToast } from 'src/types/IToaster';
import { getRateCardBreadcrumbItems } from 'src/util/RateCards';
import { SaveManager } from 'src/util/SaveManager/SaveManager';
import {
    buildDefaultCaeMap,
    buildRatchetModelMap,
    buildRegionMap,
    buildServiceLaunchDateMap
} from 'src/util/metadataUtil';

import { ConfirmationModal } from '../ConfirmationModal';
import { RateCardHeader } from '../RateCardHeader';
import { ContractTerms } from './ContractTerms';
import { FirstPartyRates } from './FirstPartyRates';
import './RateCardDetail.scss';
import { RateCardTabs } from './RateCardTabs';
import { ThirdPartyRates } from './ThirdPartyRates';
import { buildPlanMetadataLookup } from './mappers/buildPlanMetadataLookup';
import { mapContractToRateCardValues } from './mappers/mapContractToRateCardValues';

interface IRateCardDetail {
    rateCardId: number;
    isEditMode: boolean;
    isEditDraft: boolean;
    isEmbedded?: boolean;
}

export const steps: IStepTrackerStep[] = [
    {
        stepId: ROUTES.RATE_CARDS.STEP.CORE_RATES,
        stepTitle: 'Core rates',
        StepComponent: CoreRates
    },
    {
        stepId: ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS,
        stepTitle: 'Contract terms',
        StepComponent: ContractTerms
    },
    {
        stepId: ROUTES.RATE_CARDS.STEP.FIRST_PARTY_DISCOUNTED_RATES,
        stepTitle: '1p discounted rates',
        StepComponent: FirstPartyRates
    },
    {
        stepId: ROUTES.RATE_CARDS.STEP.THIRD_PARTY_DISCOUNTED_RATES,
        stepTitle: '3p discounted rates',
        StepComponent: ThirdPartyRates
    },
    {
        stepId: ROUTES.RATE_CARDS.STEP.REVIEW,
        stepTitle: 'Review and publish',
        StepComponent: RateCardTabs
    }
];

export const RateCardDetail: React.FC<IRateCardDetail> = (props) => {
    const history = useHistory();
    const { pathname } = useLocation();

    const { rateCardId, isEditMode, isEditDraft } = props;
    const isEmbedded = props.isEmbedded ?? false;
    const [isNextDisabled, setIsNextDisabled] = React.useState(false);
    const [isPrevDisabled, setIsPrevDisabled] = React.useState(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = React.useState(false);
    const [isSaveUpdatesModalOpen, setIsSaveUpdatesModalOpen] = React.useState(false);
    const [saveUpdatesComments, setSaveUpdatesComments] = React.useState('');

    const basePath =
        ROUTES.RATE_CARDS.INDEX +
        (isEditDraft ? ROUTES.RATE_CARDS.ADD : isEditMode ? ROUTES.RATE_CARDS.EDIT : '') +
        `/${rateCardId}`;

    const detailPath = `${ROUTES.RATE_CARDS.INDEX}/${rateCardId}`;

    const step = `/${pathname.split('/').pop()}`;

    const currentStepIndex = steps.findIndex(({ stepId }) => stepId === step);

    const handleToasts = (path: string) => {
        if (isEditDraft) {
            const toast: IToast =
                path === detailPath
                    ? {
                          id: 'published-rate-card-toast',
                          timeout: DEFAULT_TOASTER_TIMEOUT,
                          message: 'New rate card published',
                          type: 'success'
                      }
                    : {
                          id: 'saved-rate-card-draft-toast',
                          timeout: DEFAULT_TOASTER_TIMEOUT,
                          message: 'Saved rate card as draft',
                          type: 'success'
                      };
            setToasts([toast]);
        } else {
            const toast: IToast = {
                id: 'rate-card-updated-toast',
                timeout: DEFAULT_TOASTER_TIMEOUT,
                message: 'Rate card updated',
                type: 'success'
            };
            setToasts(path === detailPath ? [toast] : []);
        }
    };

    const navigateOnSuccess = (path: string) => {
        return (errors: string[]) => {
            if (errors.length == 0) {
                handleToasts(path);
                history.push(path);
            } else {
                setTopAlertMsg(errors.join('\n'));
            }
        };
    };

    const saveIfDraftOrReview = async (steps?: string[], isPublish?: boolean) => {
        if (isEditMode && isEditDraft) {
            return await saveChanges(steps, isPublish, fullStepList);
        } else if (step === ROUTES.RATE_CARDS.STEP.REVIEW) {
            return await saveChanges(steps, isPublish, fullStepList);
        }
        return [];
    };

    const saveThenNavigate = (path: string) => {
        saveIfDraftOrReview([step], false).then(navigateOnSuccess(path));
    };

    const saveAllThenNavigate = (path: string) => {
        saveIfDraftOrReview([step], isEditDraft).then(navigateOnSuccess(path));
    };

    const onNext = () => {
        saveThenNavigate(`${basePath}${steps[currentStepIndex + 1].stepId}`);
    };

    const onPrevious = () => {
        const prevPath = `${basePath}${steps[currentStepIndex - 1].stepId}`;
        if (!isEditDraft) {
            history.push(prevPath);
        } else {
            saveThenNavigate(prevPath);
        }
    };

    const onCancel = () => {
        setIsCancelModalOpen(true);
    };

    const onSaveDraft = () => {
        saveThenNavigate(`${basePath}${steps[currentStepIndex].stepId}`);
    };

    const onPublish = () => {
        if (isEditDraft) {
            saveAllThenNavigate(detailPath);
        } else {
            setIsSaveUpdatesModalOpen(true);
        }
    };

    const { setTopAlertMsg, userRole, userInfo, metadata, setToasts } = React.useContext(AppContext);
    const [augmentedMetadata, setAugmentedMetadata] = useState<IMetadata>(metadata);
    const [headerDataLoaded, setHeaderDataLoaded] = React.useState<boolean>(false);
    const [dataLoaded, setDataLoaded] = React.useState<boolean>(false);
    const [rateCardValues, setRateCardValues] = React.useState(defaultRateCardValues);
    const [planNameToMetadataObj, setPlanNameToMetadataObj] = React.useState<IPlanNameToMetadataObj>({});
    const [multiplierTypeList, setMultiplierTypeList] = React.useState<IMultiplierType[]>([]);
    const [corePlanList, setCorePlanList] = React.useState<IFirstPartyCorePlan[]>([]);
    const [thirdPartyPlanList, setThirdPartyPlanList] = React.useState<Record<string, string | boolean | number>[]>([]);
    const [firstPartyFreeTrialPlanList, setFirstPartyFreeTrialPlanList] = React.useState<
        Record<string, string | boolean | number>[]
    >([]);
    const [thirdPartyFreeTrialPlanList, setThirdPartyFreeTrialPlanList] = React.useState<
        Record<string, string | boolean | number>[]
    >([]);
    const [isRateCardInputValid, setIsRateCardInputValid] = React.useState(defaultIsRateCardInputValid);
    const editStepName = steps.find((stepData) => stepData.stepId === step)?.stepTitle ?? 'rate card';
    const pageAction = isEditMode ? `Edit ${editStepName}` : 'View rate card';
    useTitle(`${pageAction}: ${rateCardValues.contractName}`);

    useRateCardDetail(
        rateCardValues,
        setRateCardValues,
        multiplierTypeList,
        thirdPartyPlanList,
        corePlanList,
        augmentedMetadata,
        isEditMode
    );

    const saveProps: RateCardSaveProps = {
        contractId: rateCardId,
        user: userInfo.username,
        values: rateCardValues,
        planMetadata: planNameToMetadataObj,
        metadata: augmentedMetadata,
        firstPartyFreeTrialPlanList,
        thirdPartyFreeTrialPlanList
    };
    const { saveChanges, registerChange, isSaving } = SaveManager.trackRateCardState(saveProps, handlers);

    const setWorkflowStepValues = (stepId: string, action: SetStateAction<IRateCardValues>) => {
        registerChange(stepId);
        setRateCardValues(action);
    };

    // Augment metadata with necessary API calls
    const augmentMetadata = async () => {
        const regionsPromise = contractConfigurationClient.getRegionsById(GetRegionsByIdInput.clone({})).toPromise();
        const caePromise = contractConfigurationClient
            .getDefaultContentAcquisitionEntity(
                GetDefaultContentAcquisitionEntityInput.clone({ includeUnlaunchedTerritories: true })
            )
            .toPromise();
        const serviceLaunchDatesPromise = contractConfigurationClient
            .getServiceLaunchDate(GetServiceLaunchDateInput.clone({}))
            .toPromise();
        const royaltyModelTypesPromise = contractConfigurationClient
            .getRoyaltyModelTypes(GetRoyaltyModelTypesInput.clone({}))
            .toPromise();
        const ratchetModelsPromise = contractConfigurationClient
            .getRatchetModelsById(GetRatchetModelsByIdInput.clone({ serviceId: 1 }))
            .toPromise();

        try {
            const responses = await Promise.all([
                regionsPromise,
                caePromise,
                serviceLaunchDatesPromise,
                royaltyModelTypesPromise,
                ratchetModelsPromise
            ]);
            const [
                regionsResponse,
                caeResponse,
                serviceLaunchDatesResponse,
                royaltyModelTypesResponse,
                ratchetModelsResponse
            ] = responses;

            setAugmentedMetadata((prevMetadata) => ({
                ...prevMetadata,
                regionMapping: buildRegionMap(regionsResponse.regions ?? []),
                CaeMapping: buildDefaultCaeMap(caeResponse.defaultContentAcquisitionEntities),
                activeServiceLaunchDateMapping: buildServiceLaunchDateMap(
                    serviceLaunchDatesResponse.serviceLaunches ?? []
                ),
                royaltyModelTypes: royaltyModelTypesResponse.royaltyModelTypes ?? [],
                ratchetModelMapping: buildRatchetModelMap(ratchetModelsResponse.ratchetModels ?? [])
            }));
        } catch (error) {
            setTopAlertMsg(error.message);
        }
    };

    useEffect(() => {
        augmentMetadata().then(() => {
            const contractPromise = contractConfigurationClient
                .getContractsById(
                    GetContractsByIdInput.clone({
                        contractId: rateCardId,
                        excludeRatchetModels: true,
                        excludeRoyaltyModelTypes: true,
                        excludePlayTypes: true,
                        excludeTerritoryTerms: true,
                        royaltyModelCodes: ['PSM', 'CPP'],
                        includeDraft: true,
                        includeIneligibleContractDeals: true
                    })
                )
                .toPromise();

            let contractResult: Contract;
            contractPromise
                .then((contractResponse) => {
                    if (contractResponse.contracts && contractResponse.contracts.length > 0) {
                        contractResult = contractResponse.contracts[0];

                        setRateCardValues((prev) => ({
                            ...prev,
                            contractName: contractResult.contractName ?? '',
                            description: contractResult.description ?? '',
                            isDeleted: contractResult.isDeleted ?? false,
                            isDraft: contractResult.isDraft ?? false,
                            serviceId: contractResult.serviceId,
                            createdBy: contractResult.createdBy ?? '',
                            creationDate: contractResult.creationDate?.toString() ?? '',
                            changedBy: contractResult.changedBy ?? '',
                            changedDate: contractResult.changedDate?.toString() ?? '',
                            entityActiveIntervals: contractResult.entityActiveIntervals ?? []
                        }));

                        setHeaderDataLoaded(true);
                    }
                })
                .catch((error) => {
                    console.error('Contract header data load error', error);
                    setTopAlertMsg(error.message);
                });

            const planMetadataPromise = contractConfigurationClient
                .getPaperPlanMetadata(GetPaperPlanMetadataInput.clone({ serviceIds: [1] }))
                .toPromise();

            const territoryTermsPromise = getFullTerritoryTerms(
                GetTerritoryTermsInput.clone({
                    contractId: rateCardId
                })
            );

            const contractTermsPromise = contractConfigurationClient
                .getContractTerms(GetContractTermsInput.clone({ contractId: rateCardId }))
                .toPromise();

            const multiplierTypesPromise = contractConfigurationClient.getRateMultiplierTypes().toPromise();

            const discountRatesPromise = contractConfigurationClient
                .getDiscountRates(GetDiscountRatesInput.clone({ contractId: rateCardId }))
                .toPromise();

            Promise.all([
                planMetadataPromise,
                contractTermsPromise,
                multiplierTypesPromise,
                discountRatesPromise,
                territoryTermsPromise
            ])
                .then((response) => {
                    const [
                        planMetadataResponse,
                        contractTermsResponse,
                        multiplierTypesResponse,
                        discountRatesResponse,
                        territoryTermsResponse
                    ] = response;

                    const {
                        planMetadataLookupObj,
                        corePlanList,
                        firstPartyPlanList,
                        thirdPartyPlanList,
                        firstPartyFreeTrialPlanList,
                        thirdPartyFreeTrialPlanList
                    } = buildPlanMetadataLookup(planMetadataResponse);

                    setThirdPartyPlanList(thirdPartyPlanList);
                    setPlanNameToMetadataObj(planMetadataLookupObj);
                    setCorePlanList(corePlanList);
                    setFirstPartyFreeTrialPlanList(firstPartyFreeTrialPlanList);
                    setThirdPartyFreeTrialPlanList(thirdPartyFreeTrialPlanList);

                    const multiplierList = multiplierTypesResponse.rateMultiplierTypes?.map((multiplier) => {
                        return {
                            ...multiplier,
                            ...(MULTIPLIER_NAME_MAP.get(multiplier.name || '') || {})
                        };
                    });
                    setMultiplierTypeList(multiplierList as IMultiplierType[]);

                    const discountPlanMap = new Map(
                        discountRatesResponse.discountRates?.map((planRate) => [planRate.planId!, planRate])
                    );

                    if (contractResult && contractTermsResponse.contractTermsUnion) {
                        const contract = {
                            ...contractResult,
                            territoryTerms: territoryTermsResponse.territoryTerms
                        };
                        const contractTerms = contractTermsResponse.contractTermsUnion?.amuContractTerms || {};
                        const ratchetModelIdSet = new Set<number>();
                        contract.territoryTerms?.forEach((territoryTerm) => {
                            if (territoryTerm.rateCards) {
                                territoryTerm.rateCards.forEach((rateCard) => {
                                    if (rateCard.ratchetModels && rateCard.ratchetModels.length > 0) {
                                        rateCard.ratchetModels.forEach((ratchetModel) => {
                                            if (ratchetModel.ratchetModelId !== undefined) {
                                                ratchetModelIdSet.add(ratchetModel.ratchetModelId);
                                            }
                                        });
                                    }
                                });
                            }
                        });

                        const ratchetModelPromise = contractConfigurationClient
                            .getRatchetModelsById(
                                GetRatchetModelsByIdInput.clone({
                                    ratchetModelIds: Array.from(ratchetModelIdSet),
                                    contractId: rateCardId
                                })
                            )
                            .toPromise();

                        ratchetModelPromise.then((ratchetModelResponse: GetRatchetModelsByIdOutput) => {
                            const ratchetModels = ratchetModelResponse.ratchetModels;
                            const ratchetModelObj = {} as IRatchetModelIdtoRatchetsObj;
                            ratchetModels?.forEach((ratchetModel) => {
                                if (ratchetModel?.ratchetModelId) {
                                    ratchetModelObj[ratchetModel.ratchetModelId] = ratchetModel.ratchets || [];
                                }
                            });

                            setRateCardValues(
                                mapContractToRateCardValues(
                                    contract,
                                    contractTerms,
                                    ratchetModelObj,
                                    planMetadataLookupObj,
                                    corePlanList,
                                    firstPartyPlanList,
                                    thirdPartyPlanList,
                                    multiplierList as IMultiplierType[],
                                    discountPlanMap,
                                    augmentedMetadata,
                                    isEditMode
                                )
                            );

                            setDataLoaded(true);
                        });
                    } else {
                        setDataLoaded(true);
                    }
                })
                .catch((error) => {
                    console.error('Contract APIs Unexpected error', error);
                    setTopAlertMsg(error);
                });
        });
    }, []);

    const breadcrumbContent = !isEmbedded && (
        <BreadcrumbWrapper
            breadcrumbItems={getRateCardBreadcrumbItems(
                isEditDraft
                    ? ContractBreadcrumbTitle.AddNewContract
                    : isEditMode
                      ? ContractBreadcrumbTitle.EditContract
                      : ContractBreadcrumbTitle.ViewContract
            )}
        />
    );

    const readModeContent = (
        <Column width="100%" height="100%" spacing="300" heights={isEmbedded ? ['fill'] : ['fit', 'fill']}>
            <Column width="100%" height="100%" spacing="300" heights={isEmbedded ? ['fill'] : ['fit', 'fill']}>
                {breadcrumbContent}
                {dataLoaded && isEqual(rateCardValues, defaultRateCardValues) ? (
                    <Text type="h300">Rate card not found</Text>
                ) : (
                    <Column heights={['fit', 'fill']}>
                        {!headerDataLoaded ? (
                            <Box type="fill" spacingInset="400">
                                <Column spacing="200">
                                    <Skeleton count={5} />
                                </Column>
                            </Box>
                        ) : (
                            <RateCardHeader rateCardHeaderValues={rateCardValues}>
                                {!isEmbedded && (
                                    <Button
                                        type="link"
                                        size="small"
                                        href={`${ROUTES.RATE_CARDS.INDEX}${
                                            rateCardValues.isDraft ? ROUTES.RATE_CARDS.ADD : ROUTES.RATE_CARDS.EDIT
                                        }/${rateCardId}${ROUTES.RATE_CARDS.STEP.CORE_RATES}`}
                                        onClick={history.push}
                                        disabled={!EDITOR_ROLE_SET.has(userRole)}
                                    >
                                        <Icon className="button-icon" tokens={editTokens} />
                                        Edit contract
                                    </Button>
                                )}
                            </RateCardHeader>
                        )}
                        <Row
                            alignmentVertical="stretch"
                            wrap="down"
                            widths={['fill']}
                            spacingInset="400 none none none"
                        >
                            <RateCardTabs
                                rateCardValues={rateCardValues}
                                setRateCardValues={setRateCardValues}
                                isEditMode={false}
                                multiplierTypeList={multiplierTypeList}
                                corePlanList={corePlanList}
                                augmentedMetadata={augmentedMetadata}
                                dataLoaded={dataLoaded}
                            />
                        </Row>
                    </Column>
                )}
            </Column>
        </Column>
    );

    const editModeContent = (
        <Column width="100%" height="100%" spacing="300" heights={isEmbedded ? ['fill'] : ['fit', 'fill']}>
            {!isSaving ? (
                <Column width="100%" height="100%" spacing="300" heights={isEmbedded ? ['fill'] : ['fit', 'fill']}>
                    {breadcrumbContent}
                    {dataLoaded && isEqual(rateCardValues, defaultRateCardValues) ? (
                        <Text type="h300">Rate card not found</Text>
                    ) : (
                        <Column heights={['fit', 'fill']}>
                            {!headerDataLoaded ? (
                                <Box type="fill" spacingInset="400">
                                    <Column spacing="200">
                                        <Skeleton count={5} />
                                    </Column>
                                </Box>
                            ) : (
                                <RateCardHeader rateCardHeaderValues={rateCardValues} />
                            )}
                            <Row
                                alignmentVertical="stretch"
                                wrap="down"
                                widths={['fit', 'fill']}
                                spacingInset="400 none none none"
                            >
                                <Column minWidth="220px" alignmentHorizontal="start" spacing="200">
                                    <StepTracker
                                        steps={steps}
                                        basePath={basePath}
                                        currentStepIndex={currentStepIndex}
                                        onClick={saveThenNavigate}
                                    />
                                </Column>
                                <Column className="step-component" spacing="500">
                                    <Switch>
                                        {steps.map(({ stepId, stepTitle, StepComponent }) => (
                                            <Route key={stepId} path={`${basePath}${stepId}`}>
                                                <StepComponent
                                                    rateCardValues={rateCardValues}
                                                    setRateCardValues={(values: SetStateAction<IRateCardValues>) =>
                                                        setWorkflowStepValues(stepId, values)
                                                    }
                                                    stepTitle={stepTitle}
                                                    isEditMode={true}
                                                    isRateCardInputValid={isRateCardInputValid}
                                                    setIsRateCardInputValid={setIsRateCardInputValid}
                                                    multiplierTypeList={multiplierTypeList}
                                                    corePlanList={corePlanList}
                                                    planNameToMetadataObj={planNameToMetadataObj}
                                                    setIsNextDisabled={setIsNextDisabled}
                                                    setIsPrevDisabled={setIsPrevDisabled}
                                                    augmentedMetadata={augmentedMetadata}
                                                    dataLoaded={dataLoaded}
                                                />
                                            </Route>
                                        ))}
                                    </Switch>
                                    <Row alignmentHorizontal="end">
                                        <Button type="link" onClick={onCancel}>
                                            Cancel
                                        </Button>
                                        {isEditDraft && (
                                            <Button
                                                type="tertiary"
                                                onClick={onSaveDraft}
                                                disabled={isNextDisabled || !dataLoaded}
                                            >
                                                Save
                                            </Button>
                                        )}
                                        {currentStepIndex > 0 && (
                                            <Button
                                                type="tertiary"
                                                onClick={onPrevious}
                                                disabled={isPrevDisabled || !dataLoaded}
                                            >
                                                Previous
                                            </Button>
                                        )}
                                        {currentStepIndex < steps.length - 1 && (
                                            <Button
                                                type="primary"
                                                onClick={onNext}
                                                disabled={isNextDisabled || !dataLoaded}
                                            >
                                                Next
                                            </Button>
                                        )}
                                        {currentStepIndex === steps.length - 1 && (
                                            <Button type="primary" onClick={onPublish}>
                                                {isEditDraft ? 'Publish' : 'Save updates'}
                                            </Button>
                                        )}
                                    </Row>
                                </Column>
                            </Row>
                        </Column>
                    )}
                    <ConfirmationModal
                        isOpen={isCancelModalOpen}
                        onClose={() => setIsCancelModalOpen(false)}
                        title={`Cancel ${isEditMode ? 'edit' : 'add'} rate card`}
                        body="Are you sure you want to cancel? Any changes will be lost."
                        primaryButtonText="Yes, cancel"
                        primaryButtonOnClick={() => history.push(detailPath)}
                        secondaryButtonText="No, go back"
                        secondaryButtonOnClick={() => setIsCancelModalOpen(false)}
                    />
                    <ConfirmationModal
                        isOpen={isSaveUpdatesModalOpen}
                        onClose={() => setIsSaveUpdatesModalOpen(false)}
                        title={`Save updates`}
                        body="Are you sure you want save updates to this rate card?"
                        warning="Updates may require reruns for past accounting periods."
                        showWarning={true}
                        comments={saveUpdatesComments}
                        onCommentsChange={setSaveUpdatesComments}
                        showComments={true}
                        primaryButtonText="Yes, save updates"
                        primaryButtonOnClick={() => saveAllThenNavigate(detailPath)}
                        secondaryButtonText="Cancel"
                        secondaryButtonOnClick={() => setIsSaveUpdatesModalOpen(false)}
                    />
                </Column>
            ) : (
                <Row height="100%" alignmentHorizontal="center" alignmentVertical="center">
                    <Column alignmentHorizontal="center" alignmentVertical="center">
                        <Loader size="large" />
                        <Text type="b200" color="secondary">
                            Saving contract changes...
                        </Text>
                    </Column>
                </Row>
            )}
        </Column>
    );

    return isEditMode ? editModeContent : readModeContent;
};
